import React, { Suspense } from 'react';
import { RouteObject, Navigate } from 'react-router-dom';
import { Progress } from 'antd';
import Login from '../../components/Login';
import AuthLayout from '../../components/layouts/AuthLayout';
import MainLayout from '../../components/layouts/MainLayout';

// Lazy load page components for code splitting
const ResellerDashboardPage = React.lazy(() => import('../../components/pages/ResellerDashboardPage'));
const UsersPage = React.lazy(() => import('../../components/pages/UsersPage'));
const ResellersPage = React.lazy(() => import('../../components/pages/ResellersPage'));
const SubscribersSinglePage = React.lazy(() => import("../../components/pages/SubscribersSinglePage"));
const SubscribersBatchPage = React.lazy(() => import("../../components/pages/SubscribersBatchPage"));
const SubscribersConnectedPage = React.lazy(() => import("../../components/pages/SubscribersConnectedPage"));
const TariffPage = React.lazy(() => import("../../components/pages/TariffPage"));
const ReportPage = React.lazy(() => import("../../components/pages/ReportPage"));

import { RouteConfig } from '../routeConfig';
import { getEnvironmentConfig } from '../routeConfig';

// Helper component to wrap lazy components with Suspense
const LazyWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '200px',
      flexDirection: 'column',
      gap: '16px'
    }}>
      <Progress 
        type="circle" 
        percent={75} 
        status="active"
        size={80}
        strokeColor={{
          '0%': '#108ee9',
          '100%': '#87d068',
        }}
      />
      <div style={{ color: '#666', fontSize: '14px' }}>Loading...</div>
    </div>
  }>
    {children}
  </Suspense>
);

export const resellerRoutes: RouteConfig = {
  getRoutes: (
    isAuthenticated: boolean,
    handleLogin: (token: string) => void,
    handleLogout: () => void
  ): RouteObject[] => {
    const envConfig = getEnvironmentConfig();

    return [
      {
        path: "/login",
        element: !isAuthenticated ? 
          <Login onLogin={handleLogin} /> : 
          <Navigate to={envConfig.defaultAuthenticatedRoute} replace />
      },

      // Protected Routes with Layout - Reseller optimized
      {
        path: "/dashboard",
        element: (
          <AuthLayout isAuthenticated={isAuthenticated}>
            <MainLayout onLogout={handleLogout}>
              <LazyWrapper>
                <ResellerDashboardPage />
              </LazyWrapper>
            </MainLayout>
          </AuthLayout>
        )
      },
      {
        path: "/users",
        element: (
          <AuthLayout isAuthenticated={isAuthenticated}>
            <MainLayout onLogout={handleLogout}>
              <LazyWrapper>
                <UsersPage />
              </LazyWrapper>
            </MainLayout>
          </AuthLayout>
        )
      },
        {
            path: "/resellers",
            element: (
                <AuthLayout isAuthenticated={isAuthenticated}>
                    <MainLayout onLogout={handleLogout}>
                        <LazyWrapper>
                            <ResellersPage />
                        </LazyWrapper>
                    </MainLayout>
                </AuthLayout>
            )
        },
      {
        path: "/subscribers/single",
        element: (
            <AuthLayout isAuthenticated={isAuthenticated}>
              <MainLayout onLogout={handleLogout}>
                <LazyWrapper>
                  <SubscribersSinglePage />
                </LazyWrapper>
              </MainLayout>
            </AuthLayout>
        )
      },
      {
        path: "/subscribers/batch",
        element: (
            <AuthLayout isAuthenticated={isAuthenticated}>
              <MainLayout onLogout={handleLogout}>
                <LazyWrapper>
                  <SubscribersBatchPage />
                </LazyWrapper>
              </MainLayout>
            </AuthLayout>
        )
      },
      {
        path: "/subscribers/connected",
        element: (
            <AuthLayout isAuthenticated={isAuthenticated}>
              <MainLayout onLogout={handleLogout}>
                <LazyWrapper>
                  <SubscribersConnectedPage />
                </LazyWrapper>
              </MainLayout>
            </AuthLayout>
        )
      },
        {
            path: "/tariff",
            element: (
                <AuthLayout isAuthenticated={isAuthenticated}>
                    <MainLayout onLogout={handleLogout}>
                        <LazyWrapper>
                            <TariffPage />
                        </LazyWrapper>
                    </MainLayout>
                </AuthLayout>
            )
        },
        {
            path: "/reports",
            element: (
                <AuthLayout isAuthenticated={isAuthenticated}>
                    <MainLayout onLogout={handleLogout}>
                        <LazyWrapper>
                            <ReportPage />
                        </LazyWrapper>
                    </MainLayout>
                </AuthLayout>
            )
        },
        {
            path: "/reports/subscription",
            element: (
                <AuthLayout isAuthenticated={isAuthenticated}>
                    <MainLayout onLogout={handleLogout}>
                        <LazyWrapper>
                            <ReportPage />
                        </LazyWrapper>
                    </MainLayout>
                </AuthLayout>
            )
        },
        {
            path: "/reports/hourly-subscription",
            element: (
                <AuthLayout isAuthenticated={isAuthenticated}>
                    <MainLayout onLogout={handleLogout}>
                        <LazyWrapper>
                            <ReportPage />
                        </LazyWrapper>
                    </MainLayout>
                </AuthLayout>
            )
        },
        {
            path: "/reports/online-transaction",
            element: (
                <AuthLayout isAuthenticated={isAuthenticated}>
                    <MainLayout onLogout={handleLogout}>
                        <LazyWrapper>
                            <ReportPage />
                        </LazyWrapper>
                    </MainLayout>
                </AuthLayout>
            )
        },
      // Root redirect
      {
        path: "/",
        element: (
          <Navigate 
            to={isAuthenticated ? envConfig.defaultAuthenticatedRoute : envConfig.defaultUnauthenticatedRoute} 
            replace 
          />
        )
      },

      // 404 - Redirect to login (reseller should be strict)
      {
        path: "*",
        element: <Navigate to={envConfig.defaultUnauthenticatedRoute} replace />
      }
    ];
  }
};
